import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cartService, CartItem } from '../services/cartService';

interface CartContextType {
  // Cart data keyed by projectId
  carts: Record<string, CartData>;
  
  // Loading states
  loading: Record<string, boolean>;
  
  // Error states
  errors: Record<string, string | null>;
  
  // Actions
  fetchCart: (projectId: string) => Promise<void>;
  addToCart: (projectId: string, clientId: string, siteId: string, contentData?: { contentText?: string; contentDocUrl?: string; contentUrl?: string; orderDate?: string; submissionType?: 'GBS' | 'LI' | 'BOTH'; price?: number }) => Promise<void>;
  removeFromCart: (projectId: string, orderId: string) => Promise<void>;
  checkout: (
    projectId: string,
    orderMonth: number,
    orderYear: number,
    notes?: string,
    planId?: string,
    paymentMethod?: 'wallet',
  ) => Promise<any>;
  clearCart: (projectId: string) => Promise<void>;
  refreshCart: (projectId: string) => Promise<void>;
  clearError: (projectId: string) => void;
  
  // Utility functions
  getCartCount: (projectId: string) => number;
  getCartTotal: (projectId: string) => number;
  isInCart: (projectId: string, siteId: string) => boolean;
}

interface CartData {
  projectId: string;
  projectName: string;
  clientId: string;
  clientName: string;
  clientBudget: number;
  projectBudget: number;
  cartItems: CartItem[];
  cartTotal: number;
  cartCount: number;
  budgetRemaining: number;
  budgetUsedPercent: number;
  monthlySpent?: number;
  monthPlacementSpent?: number;
  monthOrderSpent?: number;
  lastUpdated: Date;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [carts, setCarts] = useState<Record<string, CartData>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // Set loading state for a project
  const setProjectLoading = useCallback((projectId: string, isLoading: boolean) => {
    setLoading(prev => ({ ...prev, [projectId]: isLoading }));
  }, []);

  // Set error state for a project
  const setProjectError = useCallback((projectId: string, error: string | null) => {
    setErrors(prev => ({ ...prev, [projectId]: error }));
  }, []);

  // Clear error for a project
  const clearError = useCallback((projectId: string) => {
    setProjectError(projectId, null);
  }, [setProjectError]);

  // Fetch cart items for a project
  const fetchCart = useCallback(async (projectId: string) => {
    if (!projectId) return;
    
    setProjectLoading(projectId, true);
    setProjectError(projectId, null);

    try {
      const response = await cartService.getCartItems(projectId);
      
      setCarts(prev => ({
        ...prev,
        [projectId]: {
          ...response.data,
          lastUpdated: new Date()
        }
      }));
    } catch (error: any) {
      // Don't show error for 401 (unauthorized) - user might not have access to this project
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Cart access denied for project:', projectId);
        setProjectError(projectId, null); // Clear error silently
      } else {
        const errorMessage = error.response?.data?.error || 'Failed to fetch cart items';
        setProjectError(projectId, errorMessage);
        console.error('Error fetching cart:', error);
      }
    } finally {
      setProjectLoading(projectId, false);
    }
  }, [setProjectLoading, setProjectError]);

  // Add item to cart
  const addToCart = useCallback(async (projectId: string, clientId: string, siteId: string, contentData?: { contentText?: string; contentDocUrl?: string; contentUrl?: string; orderDate?: string; submissionType?: 'GBS' | 'LI' | 'BOTH'; price?: number }) => {
    setProjectLoading(projectId, true);
    setProjectError(projectId, null);

    try {
      await cartService.addToCart({ projectId, clientId, siteId, ...contentData });
      
      // Refresh cart after adding
      await fetchCart(projectId);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to add item to cart';
      setProjectError(projectId, errorMessage);
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setProjectLoading(projectId, false);
    }
  }, [fetchCart, setProjectLoading, setProjectError]);

  // Remove item from cart
  const removeFromCart = useCallback(async (projectId: string, orderId: string) => {
    // Optimistic remove so a double-click doesn't fire DELETE twice on the same id
    let alreadyGone = false;
    setCarts((prev) => {
      const existing = prev[projectId];
      if (!existing) {
        alreadyGone = true;
        return prev;
      }
      if (!existing.cartItems.some((it) => it.id === orderId)) {
        alreadyGone = true;
        return prev;
      }
      const cartItems = existing.cartItems.filter((it) => it.id !== orderId);
      const cartTotal = cartItems.reduce((sum, it) => sum + Number(it.price || 0), 0);
      return {
        ...prev,
        [projectId]: {
          ...existing,
          cartItems,
          cartTotal,
          cartCount: cartItems.length,
          lastUpdated: new Date(),
        },
      };
    });
    if (alreadyGone) return;

    setProjectLoading(projectId, true);
    setProjectError(projectId, null);

    try {
      await cartService.removeFromCart(orderId);
      await fetchCart(projectId);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to remove item from cart';
      setProjectError(projectId, errorMessage);
      console.error('Error removing from cart:', error);
      await fetchCart(projectId);
    } finally {
      setProjectLoading(projectId, false);
    }
  }, [fetchCart, setProjectLoading, setProjectError]);

  // Checkout cart
  const checkout = useCallback(async (
    projectId: string, 
    orderMonth: number, 
    orderYear: number, 
    notes?: string,
    planId?: string,
    paymentMethod?: 'wallet',
  ) => {
    setProjectLoading(projectId, true);
    setProjectError(projectId, null);

    try {
      const response = await cartService.checkout({
        projectId,
        orderMonth,
        orderYear,
        notes,
        planId,
        paymentMethod,
      });

      // Clear cart from state after successful checkout
      setCarts(prev => {
        const newCarts = { ...prev };
        delete newCarts[projectId];
        return newCarts;
      });

      void queryClient.invalidateQueries({ queryKey: ['client-billing'] });
      void queryClient.invalidateQueries({ queryKey: ['placements'] });
      void queryClient.invalidateQueries({ queryKey: ['placement-status-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['all-placements'] });
      void queryClient.invalidateQueries({ queryKey: ['project-reporting-placements-list'] });
      if (paymentMethod === 'wallet') {
        void queryClient.invalidateQueries({ queryKey: ['wallet'] });
      }

      return response;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to complete checkout';
      setProjectError(projectId, errorMessage);
      console.error('Error during checkout:', error);
      throw error;
    } finally {
      setProjectLoading(projectId, false);
    }
  }, [setProjectLoading, setProjectError, queryClient]);

  // Clear entire cart
  const clearCart = useCallback(async (projectId: string) => {
    setProjectLoading(projectId, true);
    setProjectError(projectId, null);

    try {
      await cartService.clearCart(projectId);
      
      // Remove cart from state
      setCarts(prev => {
        const newCarts = { ...prev };
        delete newCarts[projectId];
        return newCarts;
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to clear cart';
      setProjectError(projectId, errorMessage);
      console.error('Error clearing cart:', error);
      throw error;
    } finally {
      setProjectLoading(projectId, false);
    }
  }, [setProjectLoading, setProjectError]);

  // Refresh cart (alias for fetchCart)
  const refreshCart = useCallback(async (projectId: string) => {
    await fetchCart(projectId);
  }, [fetchCart]);

  // When project settings (e.g. monthly budget) change, refresh loaded carts immediately.
  useEffect(() => {
    const onProjectUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ id?: string; budget?: number | null; projectName?: string }>)
        .detail;
      const projectId = detail?.id;
      if (!projectId) return;

      setCarts((prev) => {
        const existing = prev[projectId];
        if (!existing) return prev;
        const nextBudget =
          detail.budget != null && !Number.isNaN(Number(detail.budget))
            ? Number(detail.budget)
            : existing.projectBudget;
        const monthlySpent = existing.monthlySpent ?? 0;
        const budgetRemaining = nextBudget > 0 ? nextBudget - monthlySpent - existing.cartTotal : 0;
        const budgetUsedPercent =
          nextBudget > 0 ? ((monthlySpent + existing.cartTotal) / nextBudget) * 100 : 0;
        return {
          ...prev,
          [projectId]: {
            ...existing,
            projectBudget: nextBudget,
            projectName: detail.projectName || existing.projectName,
            budgetRemaining,
            budgetUsedPercent,
            lastUpdated: new Date(),
          },
        };
      });

      // Re-fetch for authoritative remaining / spent figures from the API.
      void fetchCart(projectId);
    };

    window.addEventListener('wf-project-updated', onProjectUpdated);
    return () => window.removeEventListener('wf-project-updated', onProjectUpdated);
  }, [fetchCart]);

  // Get cart count for a project
  const getCartCount = useCallback((projectId: string): number => {
    return carts[projectId]?.cartCount || 0;
  }, [carts]);

  // Get cart total for a project
  const getCartTotal = useCallback((projectId: string): number => {
    return carts[projectId]?.cartTotal || 0;
  }, [carts]);

  // Check if a site is in the cart
  const isInCart = useCallback((projectId: string, siteId: string): boolean => {
    const cart = carts[projectId];
    if (!cart) return false;
    return cart.cartItems.some(item => item.siteId === siteId);
  }, [carts]);

  const value: CartContextType = {
    carts,
    loading,
    errors,
    fetchCart,
    addToCart,
    removeFromCart,
    checkout,
    clearCart,
    refreshCart,
    clearError,
    getCartCount,
    getCartTotal,
    isInCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use cart context
export const useCartContext = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCartContext must be used within a CartProvider');
  }
  return context;
};

// Custom hook for a specific project's cart
export const useCart = (projectId?: string) => {
  const context = useCartContext();

  // Load cart from API whenever a project is bound (survives refresh / direct URL).
  useEffect(() => {
    if (projectId) {
      void context.fetchCart(projectId);
    }
  }, [projectId, context.fetchCart]);
  
  if (!projectId) {
    return {
      cart: null,
      loading: false,
      error: null,
      fetchCart: async () => {},
      addToCart: async () => {},
      removeFromCart: async () => {},
      checkout: async () => {},
      clearCart: async () => {},
      refreshCart: async () => {},
      clearError: () => {},
      cartCount: 0,
      cartTotal: 0,
      isInCart: () => false
    };
  }

  return {
    cart: context.carts[projectId] || null,
    loading: context.loading[projectId] || false,
    error: context.errors[projectId] || null,
    fetchCart: () => context.fetchCart(projectId),
    addToCart: (clientId: string, siteId: string, contentData?: { contentText?: string; contentDocUrl?: string; contentUrl?: string; orderDate?: string; submissionType?: 'GBS' | 'LI' | 'BOTH'; price?: number }) => context.addToCart(projectId, clientId, siteId, contentData),
    removeFromCart: (orderId: string) => context.removeFromCart(projectId, orderId),
    checkout: (
      orderMonth: number,
      orderYear: number,
      notes?: string,
      planId?: string,
      paymentMethod?: 'wallet',
    ) => context.checkout(projectId, orderMonth, orderYear, notes, planId, paymentMethod),
    clearCart: () => context.clearCart(projectId),
    refreshCart: () => context.refreshCart(projectId),
    clearError: () => context.clearError(projectId),
    cartCount: context.getCartCount(projectId),
    cartTotal: context.getCartTotal(projectId),
    isInCart: (siteId: string) => context.isInCart(projectId, siteId)
  };
};

export default CartContext;
